"use client";

/**
 * Indicador discreto del estado de red + popup con detalle y botón de sync.
 *
 * - Dot ámbar 8px al lado de las campanas del topbar cuando offline o hay
 *   items pendientes en el outbox.
 * - Click → popup con: estado de red, contador de pendientes, items con
 *   error/conflicto, botón "Sincronizar ahora".
 * - Si todo está OK (online + 0 pendientes) → no renderiza nada.
 */

import { useEffect, useState, useRef } from "react";
import { CloudOff, RefreshCw, X, AlertTriangle } from "lucide-react";
import { useNetworkStatus } from "@/lib/hooks/useNetworkStatus";
import { CHANNELS, listen } from "@/lib/offline/db";
import { listOutbox, retryItem, discardItem, type OutboxItem } from "@/lib/offline/outbox";

type Props = {
  /** Tamaño del dot en px. Default 8. */
  size?: number;
};

function formatTimeAgo(ts: number): string {
  const diffMs = Date.now() - ts;
  if (diffMs < 60_000) return "ahora";
  const min = Math.round(diffMs / 60_000);
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return `hace ${h} h`;
}

export default function OfflineDot({ size = 8 }: Props) {
  const { online, since } = useNetworkStatus();
  const [items, setItems] = useState<OutboxItem[]>([]);
  const [legacyCount, setLegacyCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [draining, setDraining] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Sincroniza listado de items del outbox + cola legacy.
  useEffect(() => {
    let cancelled = false;
    const unsubs: Array<() => void> = [];

    const refresh = async () => {
      try {
        const [outboxItems, legacyN] = await Promise.all([
          listOutbox().catch(() => [] as OutboxItem[]),
          (async () => {
            try {
              const mod = await import("@/lib/offline-queue");
              return await mod.queueSize();
            } catch {
              return 0;
            }
          })(),
        ]);
        if (!cancelled) {
          setItems(outboxItems);
          setLegacyCount(legacyN);
        }
      } catch {
        /* ignore */
      }
    };

    refresh();
    unsubs.push(listen(CHANNELS.outbox, refresh));

    (async () => {
      try {
        const mod = await import("@/lib/offline-queue");
        if (typeof mod.listenQueueChange === "function") {
          unsubs.push(mod.listenQueueChange(refresh));
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
      unsubs.forEach((u) => u());
    };
  }, []);

  // Cerrar al click fuera.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pendingCount = items.length + legacyCount;
  const failedItems = items.filter((i) => i.status === "failed" || i.status === "conflict");

  if (online && pendingCount === 0) return null;

  const handleSyncNow = async () => {
    setDraining(true);
    try {
      const mod = await import("@/lib/offline/drainer");
      await mod.drainOutbox();
      // Drena también la cola legacy si existe.
      try {
        const legacy = await import("@/lib/offline-queue");
        await legacy.flushQueue();
      } catch {
        /* ignore */
      }
    } catch {
      /* ignore */
    } finally {
      setDraining(false);
    }
  };

  const handleRetry = async (id: number) => {
    await retryItem(id);
    handleSyncNow();
  };

  const handleDiscard = async (id: number) => {
    if (!confirm("¿Descartar esta acción pendiente? Se perderá el cambio.")) return;
    await discardItem(id);
  };

  const title = !online
    ? `Sin conexión — desde ${formatTimeAgo(since)}${pendingCount > 0 ? ` · ${pendingCount} pendiente${pendingCount === 1 ? "" : "s"}` : ""}`
    : `${pendingCount} pendiente${pendingCount === 1 ? "" : "s"} por sincronizar`;

  return (
    <div className="relative" ref={popupRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={title}
        aria-label={title}
        className="inline-flex items-center gap-1 select-none p-1 rounded hover:bg-surface-hover transition"
      >
        <span
          className="rounded-full bg-amber-500"
          style={{ width: size, height: size }}
        />
        {!online && (
          <CloudOff
            size={Math.max(12, size + 4)}
            className="text-amber-400"
            aria-hidden
          />
        )}
        {pendingCount > 0 && (
          <span className="text-[10px] font-bold text-amber-400">{pendingCount}</span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop semi-transparente — sólo en móvil */}
          <div
            className="fixed inset-0 z-40 bg-black/30 sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="
            z-50 rounded-xl border border-border bg-surface shadow-xl p-3 space-y-3
            fixed left-1/2 -translate-x-1/2 top-14 w-[calc(100vw-1rem)] max-w-sm
            sm:fixed sm:right-4 sm:top-14 sm:left-auto sm:translate-x-0 sm:w-80
          ">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-text">
                {online ? "Conectado" : "Sin conexión"}
              </p>
              <p className="text-[10px] text-text-muted">
                {pendingCount === 0
                  ? "Sin pendientes"
                  : `${pendingCount} pendiente${pendingCount === 1 ? "" : "s"} por sincronizar`}
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-text-muted hover:text-text"
              aria-label="Cerrar"
            >
              <X size={14} />
            </button>
          </div>

          {pendingCount > 0 && online && (
            <button
              type="button"
              onClick={handleSyncNow}
              disabled={draining}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-accent/30 bg-accent/10 hover:bg-accent/20 px-3 py-2 text-xs font-medium text-accent disabled:opacity-50 transition"
            >
              <RefreshCw size={14} className={draining ? "animate-spin" : ""} />
              {draining ? "Sincronizando…" : "Sincronizar ahora"}
            </button>
          )}

          {failedItems.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wider flex items-center gap-1">
                <AlertTriangle size={10} /> Con errores
              </p>
              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                {failedItems.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-md border border-red-500/20 bg-red-500/5 p-2 text-[10px] space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-text-muted">
                        {item.method} {item.kind}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded font-semibold ${item.status === "conflict" ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"}`}>
                        {item.status === "conflict" ? "Conflicto" : "Falló"}
                      </span>
                    </div>
                    {item.lastError && (
                      <p className="text-text-muted line-clamp-2">{item.lastError}</p>
                    )}
                    <div className="flex gap-2">
                      {item.id != null && (
                        <button
                          onClick={() => handleRetry(item.id!)}
                          className="text-accent hover:underline"
                        >
                          Reintentar
                        </button>
                      )}
                      {item.id != null && (
                        <button
                          onClick={() => handleDiscard(item.id!)}
                          className="text-red-400 hover:underline"
                        >
                          Descartar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!online && pendingCount > 0 && (
            <p className="text-[10px] text-text-muted text-center">
              Se sincronizará automáticamente al reconectar.
            </p>
          )}
          </div>
        </>
      )}
    </div>
  );
}
